import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, LogIn } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { Logo } from "../../components/site/Logo";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/admin", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-paper border border-navy/15 px-5 py-4 text-sm text-navy placeholder:text-subtle/70 focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent transition-shadow duration-300";

  return (
    <div className="min-h-screen bg-paper flex items-center justify-center px-6 relative" data-testid="admin-login-page">
      <div className="grain-overlay" />
      <div className="absolute inset-0 blueprint-grid opacity-40 pointer-events-none" />
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="relative w-full max-w-md border border-navy/10 bg-white shadow-xl shadow-navy/5 p-10"
      >
        <div className="flex items-center">
          <Logo markClass="h-12 w-auto" />
        </div>
        <h1 className="font-display font-bold tracking-tight text-3xl mt-8">Admin Login</h1>
        <p className="text-subtle text-sm mt-2">Sign in to view website enquiries.</p>

        <form onSubmit={submit} className="mt-8 space-y-5" data-testid="admin-login-form">
          <input
            data-testid="admin-email-input"
            type="email"
            required
            placeholder="Email Address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={inputCls}
          />
          <input
            data-testid="admin-password-input"
            type="password"
            required
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={inputCls}
          />
          {error && (
            <p data-testid="admin-login-error" className="text-sm text-red-400 border border-red-400/30 bg-red-400/10 px-4 py-3">
              {error}
            </p>
          )}
          <motion.button
            data-testid="admin-login-submit"
            type="submit"
            disabled={loading}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="w-full bg-brand text-white font-semibold py-4 hover:bg-brand-hover transition-colors duration-300 flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {loading ? <Loader2 size={18} className="animate-spin" /> : <LogIn size={18} />}
            {loading ? "Signing in…" : "Sign In"}
          </motion.button>
        </form>

        <Link to="/" data-testid="admin-login-back-link" className="block text-center text-xs uppercase tracking-[0.2em] text-subtle hover:text-white transition-colors duration-300 mt-8">
          Back to Website
        </Link>
      </motion.div>
    </div>
  );
}
